import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { EChartsOption } from 'echarts';
import type { GolfHoleScoreDto, GolfScoreDetailDto } from '@jordan/types';
import { Alert, Breadcrumb, Button, EChart, PageHeader, Spinner } from '@jordan/ui';
import { AppChrome } from '../../../components/AppChrome';
import { ApiError, apiGet } from '../../../lib/api';
import { golfSidebar } from './golfSidebar';

const longDate = (ymd: string) =>
  ymd ? new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '—';
const num = (n: number | null, d = 0) => (n == null ? '—' : n.toFixed(d));
const sum = (holes: GolfHoleScoreDto[], f: (h: GolfHoleScoreDto) => number | null) =>
  holes.reduce((a, h) => a + (f(h) ?? 0), 0);
const toPar = (n: number) => (n === 0 ? 'E' : n > 0 ? `+${n}` : String(n));

// Golf convention: circle for under par, square for over; doubled for eagle / double-bogey+.
function scoreDeco(gross: number | null, par: number | null): CSSProperties {
  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 26,
    height: 26,
    padding: '0 4px',
  };
  if (gross == null || par == null) return base;
  const d = gross - par;
  if (d <= -2) return { ...base, borderRadius: '50%', border: '2px double #ef4444', color: '#ef4444', fontWeight: 600 };
  if (d === -1) return { ...base, borderRadius: '50%', border: '1px solid #ef4444', color: '#ef4444' };
  if (d === 0) return base;
  if (d === 1) return { ...base, border: '1px solid #94a3b8' };
  return { ...base, border: '2px solid #475569', fontWeight: 600 };
}

const firIcon = (fir: string | null) => {
  if (fir == null) return <span className="text-muted">—</span>;
  if (fir === 'Hit') return <i className="ph-check text-success" title="Fairway hit" />;
  return <i className="ph-x text-danger" title={fir.replace('Missed', 'Missed ')} />;
};

const pctLabel = (n: number | null) => (n == null ? 'N/A' : `${Math.round(n * 100)}%`);

/** A donut matching Golf Canada's stat panels: coloured slices, right-side legend, centred % + count. */
function donutOption(
  slices: { name: string; value: number; color: string }[],
  centerPct: number | null,
  centerSub: string,
): EChartsOption {
  return {
    textStyle: { fontFamily: 'var(--body-font-family)', color: 'var(--body-color)' },
    tooltip: { trigger: 'item', formatter: '{b}: {c}' },
    title: {
      text: pctLabel(centerPct),
      subtext: centerSub,
      left: '32%',
      top: 'center',
      textAlign: 'center',
      textVerticalAlign: 'middle',
      textStyle: { fontSize: 22, fontWeight: 700, color: 'var(--body-color)' },
      subtextStyle: { fontSize: 11, color: 'rgba(var(--body-color-rgb), .6)' },
    },
    legend: {
      orient: 'vertical',
      right: 4,
      top: 'center',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: 'var(--body-color)', fontSize: 11 },
    },
    series: [
      {
        type: 'pie',
        radius: ['58%', '82%'],
        center: ['32%', '50%'],
        avoidLabelOverlap: true,
        label: { show: false },
        data: slices.map((s) => ({ name: s.name, value: s.value, itemStyle: { color: s.color } })),
      },
    ],
  } as EChartsOption;
}

/** One Recovery row: label, "hit of opp", and a percentage badge (N/A when no opportunities). */
function RecoveryRow({ label, hit, opp, percent }: { label: string; hit: number; opp: number; percent: number | null }) {
  return (
    <div className="d-flex align-items-center py-2 border-bottom">
      <span className="fw-semibold me-auto">{label}</span>
      <span className="text-muted me-3">
        {hit} of {opp}
      </span>
      <span className="badge bg-primary">{pctLabel(percent)}</span>
    </div>
  );
}

/** One scorecard block (front nine, back nine, or a 9-hole round) with an OUT/IN subtotal. */
function ScorecardTable({ holes, subtotalLabel }: { holes: GolfHoleScoreDto[]; subtotalLabel: string }) {
  const cols = [...holes].sort((a, b) => a.holeNumber - b.holeNumber);
  const cell = 'text-center';
  // Penalties are only recorded on some rounds; the row would otherwise be a line of dashes.
  const anyPenalty = cols.some((h) => h.penalty != null);
  return (
    <table className="table table-bordered table-sm mb-0 text-nowrap">
      <thead>
        <tr className="table-light">
          <th>Hole</th>
          {cols.map((h) => (
            <th key={h.holeNumber} className={cell}>
              {h.holeNumber}
            </th>
          ))}
          <th className={`${cell} table-secondary`}>{subtotalLabel}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th className="text-muted fw-normal">Par</th>
          {cols.map((h) => (
            <td key={h.holeNumber} className={cell}>
              {h.par ?? '—'}
            </td>
          ))}
          <td className={`${cell} fw-semibold table-secondary`}>{sum(cols, (h) => h.par)}</td>
        </tr>
        <tr>
          <th className="text-muted fw-normal">Score</th>
          {cols.map((h) => (
            <td key={h.holeNumber} className={cell}>
              <span style={scoreDeco(h.gross, h.par)}>{h.gross ?? '—'}</span>
            </td>
          ))}
          <td className={`${cell} fw-bold table-secondary`}>{sum(cols, (h) => h.gross)}</td>
        </tr>
        <tr>
          <th className="text-muted fw-normal">Adj</th>
          {cols.map((h) => (
            <td key={h.holeNumber} className={cell}>
              {h.esc ?? '—'}
            </td>
          ))}
          <td className={`${cell} table-secondary`}>{sum(cols, (h) => h.esc)}</td>
        </tr>
        <tr>
          <th className="text-muted fw-normal">Putts</th>
          {cols.map((h) => (
            <td key={h.holeNumber} className={cell}>
              {h.putts ?? '—'}
            </td>
          ))}
          <td className={`${cell} table-secondary`}>{sum(cols, (h) => h.putts)}</td>
        </tr>
        <tr>
          <th className="text-muted fw-normal">FIR</th>
          {cols.map((h) => (
            <td key={h.holeNumber} className={cell}>
              {firIcon(h.fir)}
            </td>
          ))}
          <td className="table-secondary" />
        </tr>
        {anyPenalty && (
          <tr>
            <th className="text-muted fw-normal">Penalties</th>
            {cols.map((h) => (
              <td key={h.holeNumber} className={cell}>
                {h.penalty ? <span className="text-danger fw-semibold">{h.penalty}</span> : <span className="text-muted">—</span>}
              </td>
            ))}
            <td className={`${cell} table-secondary`}>{sum(cols, (h) => h.penalty)}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

const InfoStat = ({ label, value, danger }: { label: string; value: string; danger?: boolean }) => (
  <div className="col text-center">
    <div className={`fs-3 fw-bold ${danger ? 'text-danger' : ''}`}>{value}</div>
    <div className="text-muted fs-sm text-uppercase">{label}</div>
  </div>
);

export function GolfScoreDetailPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { id } = useParams<{ id: string }>();

  const [detail, setDetail] = useState<GolfScoreDetailDto | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    setErr(null);
    setDetail(null);
    apiGet<GolfScoreDetailDto>(`/api/health/golf/golfcanada/scores/${id}`)
      .then(setDetail)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 409) {
          setErr(
            e.body && typeof e.body === 'object' && 'message' in e.body
              ? String((e.body as { message: unknown }).message)
              : 'Sign in to Golf Canada to view round details.',
          );
        } else {
          setErr('Could not load the round details.');
        }
      });
  }, [id]);
  useEffect(load, [load]);

  const holes = detail?.holes ?? [];
  const front = holes.filter((h) => h.holeNumber <= 9);
  const back = holes.filter((h) => h.holeNumber >= 10 && h.holeNumber <= 18);
  const st = detail?.stats ?? null;

  return (
    <AppChrome secondarySidebar={golfSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Golf - <span className="fw-normal">Round detail</span>
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[
              { icon: 'ph-house', href: '/' },
              { label: 'Golf' },
              { label: 'Scores' },
              { label: 'Round' },
            ]}
          />
        }
      />

      <div className="content">
        <div className="mb-3">
          <Button variant="light" size="sm" icon="ph-arrow-left" onClick={() => navigate('/health/golf/scores')}>
            Return to scores
          </Button>
        </div>

        {err && (
          <Alert variant="warning" icon="ph-info">
            {err}
          </Alert>
        )}

        {!detail && !err && <Spinner />}

        {detail && (
          <>
            {/* Header / main info */}
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body">
                <div className="row g-4">
                  <div className="col-lg-5">
                    <h4 className="mb-1">{detail.name ?? '—'}</h4>
                    <div className="fst-italic text-muted">{detail.course ?? 'Course unknown'}</div>
                    <div className="text-muted">{longDate(detail.playDate)}</div>
                    <dl className="row mb-0 mt-3 fs-sm">
                      <dt className="col-5 text-muted fw-normal">Tee</dt>
                      <dd className="col-7">{detail.tee ?? '—'}</dd>
                      <dt className="col-5 text-muted fw-normal">Rating / Slope</dt>
                      <dd className="col-7">
                        {detail.rating != null && detail.slope != null ? `${detail.rating.toFixed(1)} / ${detail.slope}` : '—'}
                      </dd>
                      <dt className="col-5 text-muted fw-normal">Par</dt>
                      <dd className="col-7">{detail.par ?? '—'}</dd>
                      <dt className="col-5 text-muted fw-normal">Yardage</dt>
                      <dd className="col-7">{detail.yardage ?? '—'}</dd>
                      <dt className="col-5 text-muted fw-normal">Type / Format</dt>
                      <dd className="col-7">
                        {(detail.type ?? '—')}
                        {detail.format ? ` · ${detail.format}` : ''}
                      </dd>
                      {detail.postedDate && (
                        <>
                          <dt className="col-5 text-muted fw-normal">Posted</dt>
                          <dd className="col-7">{new Date(detail.postedDate).toLocaleDateString()}</dd>
                        </>
                      )}
                    </dl>
                  </div>
                  <div className="col-lg-7 d-flex align-items-center">
                    <div className="row w-100 g-2">
                      <InfoStat label="Gross" value={num(detail.gross)} />
                      <InfoStat label="Adj (ESC)" value={num(detail.esc)} />
                      <InfoStat label="To Par" value={detail.toPar != null ? toPar(detail.toPar) : '—'} />
                      <InfoStat label="Differential" value={num(detail.differential, 1)} danger />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Scorecard */}
            {detail.isHoleByHole && holes.length > 0 ? (
              <div className="card border-0 shadow-sm mb-3">
                <div className="card-header bg-transparent">
                  <h6 className="mb-0 fw-semibold">Hole scores</h6>
                </div>
                <div className="card-body">
                  <div className="table-responsive mb-3">
                    <ScorecardTable holes={front.length ? front : holes} subtotalLabel={back.length ? 'OUT' : 'TOT'} />
                  </div>
                  {back.length > 0 && (
                    <div className="table-responsive">
                      <ScorecardTable holes={back} subtotalLabel="IN" />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <Alert variant="info" icon="ph-info">
                This round was posted as a total score, so there’s no hole-by-hole breakdown.
              </Alert>
            )}

            {/* Statistics */}
            {st && (
              <div className="row g-3">
                <div className="col-lg-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-transparent">
                      <h6 className="mb-0 fw-semibold">Greens in Regulation</h6>
                    </div>
                    <div className="card-body">
                      <EChart
                        option={donutOption(
                          [
                            { name: 'Hit', value: st.girHit, color: '#7ba7bc' },
                            { name: 'Miss', value: Math.max(0, st.girOpp - st.girHit), color: '#d9dde1' },
                          ],
                          st.girPercent,
                          `${st.girHit} of ${st.girOpp}`,
                        )}
                        height={170}
                      />
                      <dl className="row mb-0 mt-2 fs-sm">
                        <dt className="col-7 text-muted fw-normal">Total Putts</dt>
                        <dd className="col-5 text-end mb-1">{st.putts ?? '—'}</dd>
                        <dt className="col-7 text-muted fw-normal">Per Hole</dt>
                        <dd className="col-5 text-end mb-1">{st.puttsPerHole?.toFixed(1) ?? '—'}</dd>
                        <dt className="col-7 text-muted fw-normal">Proximity</dt>
                        <dd className="col-5 text-end mb-0">{st.proximity?.toFixed(1) ?? '—'}</dd>
                      </dl>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-transparent">
                      <h6 className="mb-0 fw-semibold">Driving Accuracy</h6>
                    </div>
                    <div className="card-body">
                      <EChart
                        option={donutOption(
                          [
                            { name: 'Hit', value: st.firHit, color: '#7ba7bc' },
                            { name: 'Long', value: st.firLong, color: '#aeb6bd' },
                            { name: 'Right', value: st.firRight, color: '#ef4444' },
                            { name: 'Short', value: st.firShort, color: '#14b8a6' },
                            { name: 'Left', value: st.firLeft, color: '#f59e0b' },
                            { name: 'Other', value: st.firOther, color: '#64748b' },
                          ],
                          st.firPercent,
                          `${st.firHit} of ${st.firOpp}`,
                        )}
                        height={210}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-transparent">
                      <h6 className="mb-0 fw-semibold">Recovery</h6>
                    </div>
                    <div className="card-body pt-1">
                      <RecoveryRow label="Up/Down" hit={st.upDownHit} opp={st.upDownOpp} percent={st.upDownPercent} />
                      <RecoveryRow label="Sand Save" hit={st.sandSaveHit} opp={st.sandSaveOpp} percent={st.sandSavePercent} />
                      <RecoveryRow label="Scrambling" hit={st.scrambleHit} opp={st.scrambleOpp} percent={st.scramblePercent} />
                      <RecoveryRow
                        label="Bounce Back"
                        hit={st.bounceBackHit}
                        opp={st.bounceBackOpp}
                        percent={st.bounceBackPercent}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AppChrome>
  );
}
